import React, {createContext, useEffect} from "react";

import { usePage } from "@inertiajs/inertia-react";
import { createGlobalStyle, ThemeContext } from "styled-components";
import { DynamicColorPreset } from "./DynamicColorPreset";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AmazonLayout from "./Layouts/AmazonLayout";
import GoogleLayout from "./Layouts/GoogleLayout";
import WhitelabelLayout from "./Layouts/WhitelabelLayout";
import B2XLayout from "./Layouts/B2XLayout";
import DefaultLayout from "./Layouts/DefaultLayout";
import BissellLayout from "./Layouts/BissellLayout";
import B2XCarbonLayout from "./Layouts/B2XCarbonLayout";
import NiuLayout from "./Layouts/NiuLayout";
import i18n from "../utils/i18n";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import de from 'dayjs/locale/de';
import en from 'dayjs/locale/en';
import UnzerLayout from "./Layouts/UnzerLayout";
import DemoLayout from "./Layouts/DemoLayout";
import SegwayLayout from "./Layouts/SegwayLayout";
import Demo2Layout from "./Layouts/Demo2Layout";
import DemoLayoutMediamarkt from "./Layouts/DemoLayoutMediamarkt";
import DemoLayoutNothing from "./Layouts/DemoLayoutNothing";



const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    font-family: Titillium Web, Raleway, sans-serif;
  }

  html {
    margin: 0;
    padding: 0;
    font-size: 16px;
  }

  body {
    margin: 0;
    padding: 0;
    //margin-bottom: 50px;
    font-size: 16px;
    color: ${(props) => props.theme.text};
    font-family: Titillium Web, Raleway, sans-serif;
    font-weight: 300;
  }
`;

const InertiaState = createContext();
const queryClient = new QueryClient();

const useClientLayout = (clientKey) => {
    switch (clientKey) {
        case "demo_mediamarkt":
            return DemoLayoutMediamarkt;
        case "demo_unzer":
            return B2XLayout;
        case "demo_nothing":
            return DemoLayoutNothing;
        case "niu":
            return NiuLayout;
        case "unzer":
            return UnzerLayout;
        case "b2x_carbon":
            return B2XCarbonLayout;
        case "amazon":
            return AmazonLayout;
        case "google":
            return GoogleLayout;
        case "whitelabelsub":
            return WhitelabelLayout;
        case "segway":
        case "segway-de":
        case "segway-espt":
            return SegwayLayout;
        case "whitelabel":
        case "xiaomi":
            return B2XLayout;
        case "bissell":
            return BissellLayout;
        default:
            if (clientKey?.substring(0, 5) === "demo_") {
                return DemoLayout;
            }
            return DefaultLayout;
    }
};

export default function Layout({ children, wrapperStyle }) {
    const {
        props: { client },
    } = usePage();

    useEffect(() => {const preferedLanguage = Cookies.get('lang') ?? navigator.language ?? window.defaultLanguage ?? 'en';
        const availableLanguages = window.availableLanguages ?? ['en'];
        const language = availableLanguages.includes(preferedLanguage.slice(0,2)) ? preferedLanguage : availableLanguages[0];

        if (language === 'de') {
            dayjs.locale({
                ...de,
                weekStart: 1,
            });
            return;
        }
        dayjs.locale({
            ...en,
            weekStart: 1,
        });
    }, []);

    const ClientLayout = useClientLayout(client?.key);

    return (
        <QueryClientProvider client={queryClient}>
            <InertiaState.Provider value={children.props}>
                <DynamicColorPreset client={children.props.client}>
                    <GlobalStyle />
                    <ClientLayout wrapperStyle={wrapperStyle}>{children}</ClientLayout>
                </DynamicColorPreset>
            </InertiaState.Provider>
        </QueryClientProvider>
    );
}

export function useInertiaState() {
    const context = React.useContext(InertiaState);
    if (!context) {
        throw new Error("useUserState must be used within the AppProvider");
    }
    return context;
}
